import { useContext } from 'react';

import { useSelector } from 'react-redux';

import { DiffContent } from '@atlassian/bitkit-diff';

import { ApiCommentOrPlaceholder } from 'src/components/conversation-provider/types';
import { useConversationsContext } from 'src/contexts/conversations-context';
import { getCurrentPullRequestSourceBranchPermissions } from 'src/redux/pull-request/selectors';
import {
  DiffStateContext,
  SelectedDiffType,
} from 'src/sections/repository/sections/pull-request/components/tabs/diff/diff-state-context';
import { useMergeData } from 'src/sections/repository/sections/pull-request/hooks/merge-data';
import { getCurrentUser } from 'src/selectors/user-selectors';

import { CannotApplyReasons } from './types';
import { getDiffContentWithoutPrefix } from './utils';

// Determines the reason why a code suggestion cannot be applied,
// based on the state of the current pull request and the comment associated
// with the suggestion. This returns a `CannotApplyReasons` enum value if the
// suggestion cannot be applied, or `null` if the suggestion can be applied.
export function useCannotApplySuggestionReason(
  apiComment: ApiCommentOrPlaceholder | undefined,
  diffLine: DiffContent,
  suggestionText: string
): CannotApplyReasons | null {
  const conversationContext = useConversationsContext();
  const { pullRequest } = conversationContext;
  const isCommentOnPr = pullRequest !== undefined;
  const isPrStateOpenOrDraft =
    pullRequest?.state === 'OPEN' || pullRequest?.state === 'DRAFT';
  const { iterativeReviewSelectedDiff } = useContext(DiffStateContext);
  const sourceBranchPermissions = useSelector(
    getCurrentPullRequestSourceBranchPermissions
  );
  const { isMergePending } = useMergeData();
  const currentUser = useSelector(getCurrentUser);
  const branchAllowsPush = sourceBranchPermissions?.push === 'allow';
  const originalLine = getDiffContentWithoutPrefix(diffLine);

  // the order of these conditions matters! It will determine which tooltip
  // text is shown to the user over the disabled button
  if (!isCommentOnPr) {
    return CannotApplyReasons.INVALID_COMMIT_COMMENT;
  } else if (!isPrStateOpenOrDraft) {
    return CannotApplyReasons.INVALID_PR_STATE;
  } else if (!currentUser || !branchAllowsPush) {
    return CannotApplyReasons.NOT_PERMITTED;
  } else if (isMergePending) {
    return CannotApplyReasons.PENDING_MERGE;
  } else if (
    iterativeReviewSelectedDiff.selectedDiffType !== SelectedDiffType.None &&
    iterativeReviewSelectedDiff.selectedDiffType !== SelectedDiffType.All
  ) {
    return CannotApplyReasons.INVALID_DIFF_TYPE;
  } else if (apiComment?.pending) {
    return CannotApplyReasons.INVALID_COMMENT_STATE;
  } else if (!apiComment?.inline?.to) {
    return CannotApplyReasons.INVALID_REMOVED_LINE;
  } else if (apiComment?.inline?.outdated) {
    return CannotApplyReasons.INVALID_OR_ORPHANED_COMMENT;
  } else if (suggestionText === originalLine) {
    return CannotApplyReasons.REDUNDANT_SUGGESTION;
  }

  // if we return null then the suggestion can be applied!
  return null;
}
